import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  NbButtonModule,
  NbCardModule,
  NbIconModule,
  NbToastrService,
  NbDialogService,
} from '@nebular/theme';
import { finalize } from 'rxjs';
import { Turma } from '../../core/api/turma';
import { Pessoa } from '../../core/api/pessoa';
import { TurmaCatequizandoDto } from '../../core/api/turma-catequizando.dto';
import { TurmaService } from '../../core/api/turma.service';
import { PessoaService } from '../../core/api/pessoa.service';
import { TurmaCatequizandoService } from '../../core/api/turma-catequizando.service';
import { DialogAlertComponent } from './turma-pessoa.dialog.component';
import { DialogTransferirPessoaComponent } from './pessoa-turma-transferir/transferir-pessoa-turma.dialog.nt';
import { AutocompleteComponent, OpcaoAutocomplete } from '../../shared/autocomplete/autocomplete.component';

import { mensagemDeErro } from '../../utils/mensagem-erro';
import { ListaVaziaComponent } from '../../shared/lista-vazia/lista-vazia.component';
import { BarraCarregandoComponent } from '../../shared/barra-carregando/barra-carregando.component';
import { Ordenacao, OrdenavelComponent } from '../../shared/ordenavel/ordenavel.component';
import { ordenarEmMemoria } from '../../utils/ordenar-em-memoria';
@Component({
  selector: 'app-turma-pessoa',
  standalone: true,
  imports: [
    CommonModule,
    NbCardModule,
    NbButtonModule,
    NbIconModule,
    AutocompleteComponent,
    ListaVaziaComponent,
    BarraCarregandoComponent,
    OrdenavelComponent,
  ],
  templateUrl: './turma-pessoa.component.html',
  styleUrls: ['./turma-pessoa.component.scss'],
})
export class TurmaPessoaComponent implements OnInit {
  /**
   * `TAB-01` — ordenação **em memória**, e aqui ela é legítima: o endpoint devolve a
   * coleção inteira, não uma página. Onde há paginação, ordenar em memória mentiria.
   */
  ordenacao: Ordenacao | null = null;

  ordenarPor(o: Ordenacao): void {
    this.ordenacao = o;
  }

  get matriculasExibidas(): TurmaCatequizandoDto[] {
    return ordenarEmMemoria(this.matriculas, this.ordenacao, (item, campo) => (item as unknown as Record<string, unknown>)[campo]);
  }

  turmas: Turma[] = [];
  /** Turma escolhida, guardada à parte do resultado da busca. */
  turmaEscolhida?: Turma;
  carregandoTurmas = false;
  /** Matrículas da turma selecionada (projeção de `GET /turmas-catequizandos?turmaId=`). */
  matriculas: TurmaCatequizandoDto[] = [];
  /** Pessoas sem turma no ano da turma escolhida — as candidatas à matrícula. */
  pessoasSemTurma: Pessoa[] = [];
  pessoaSelecionada?: Pessoa;
  turmaSelecionada = '';
  carregandoPessoas = false;
  buscandoPessoas = false;

  constructor(
    private turmaService: TurmaService,
    private pessoaService: PessoaService,
    private matriculaService: TurmaCatequizandoService,
    private toastrService: NbToastrService,
    private dialogService: NbDialogService
  ) { }

  ngOnInit(): void {
    this.carregarTurmas();
  }

  /**
   * A matrícula pertence à turma, e o ano vem da turma (não há mais ano avulso no vínculo).
   * É esse ano que alimenta o `GET /pessoas/sem-turma?ano=`.
   *
   * Vem da turma **escolhida**, guardada à parte: com a busca no servidor, `turmas` é só o
   * resultado da última digitação e pode não conter mais a que está selecionada.
   */
  get anoTurma(): number {
    return this.turmaEscolhida?.ano ?? new Date().getFullYear();
  }

  get opcoesTurma(): OpcaoAutocomplete[] {
    return this.turmas.map((t) => ({ id: t.id ?? '', rotulo: this.labelTurma(t) }));
  }

  /** O CPF entra no rótulo porque é por ele que se busca quando há nomes repetidos. */
  get opcoesPessoa(): OpcaoAutocomplete[] {
    return this.pessoasSemTurma.map((p) => ({
      id: p.id ?? '',
      rotulo: `${p.nome} (${p.cpf || 'sem CPF'})`,
    }));
  }

  get pessoaSelecionadaId(): string {
    return this.pessoaSelecionada?.id ?? '';
  }

  labelTurma(t: Turma): string {
    const nome = (t.nome ?? '').trim();
    return t.ano != null ? `${nome} (${t.ano})` : nome;
  }

  /**
   * `FRM-04` — o termo vai para o servidor. Antes a tela carregava as 100 primeiras turmas e
   * filtrava em memória: da turma 101 em diante, o campo dizia "nenhuma encontrada" para uma
   * turma que existe.
   */
  carregarTurmas(termo = '') {
    this.carregandoTurmas = true;
    this.turmaService
      .listar(0, 20, termo)
      .pipe(finalize(() => (this.carregandoTurmas = false)))
      .subscribe((res) => {
        const achadas = res.content ?? [];
        // A escolhida continua na lista mesmo fora do resultado, senão o campo perde o rótulo.
        const escolhida = this.turmaEscolhida;
        this.turmas =
          escolhida && !achadas.some((t) => t.id === escolhida.id)
            ? [escolhida, ...achadas]
            : achadas;
      });
  }

  onTurmaChange(turmaId: string) {
    this.turmaSelecionada = turmaId;
    this.turmaEscolhida = this.turmas.find((t) => t.id === turmaId);
    this.pessoaSelecionada = undefined;
    this.carregarMatriculas();
    this.carregarPessoasSemTurma();
  }

  carregarMatriculas() {
    if (!this.turmaSelecionada) {
      this.matriculas = [];
      return;
    }

    this.carregandoPessoas = true;
    this.matriculas = [];

    this.matriculaService.listar(this.turmaSelecionada).subscribe({
      next: (matriculas) => {
        this.matriculas = matriculas;
        this.carregandoPessoas = false;
      },
      error: (err) => {
        this.matriculas = [];
        this.carregandoPessoas = false;
        this.toastrService.danger(mensagemDeErro(err, 'Não foi possível carregar as matrículas.'), 'Erro');
      },
    });
  }

  /**
   * As candidatas vêm de uma chamada só, na troca de turma: `GET /pessoas/sem-turma?ano=`
   * não recebe termo, então filtrar é trabalho do campo, em memória (`FRM-04`). Antes a
   * tela refazia essa mesma chamada a cada tecla digitada.
   */
  carregarPessoasSemTurma() {
    if (!this.turmaSelecionada) {
      this.pessoasSemTurma = [];
      return;
    }

    this.buscandoPessoas = true;
    this.pessoaService
      .listarSemTurma(this.anoTurma)
      .pipe(finalize(() => (this.buscandoPessoas = false)))
      .subscribe({
        next: (pessoas) => (this.pessoasSemTurma = pessoas),
        error: (err) => {
          // MSG-02: falha de busca não some calada — sem isso o campo dizia "nenhuma
          // pessoa" quando na verdade a chamada tinha falhado.
          this.pessoasSemTurma = [];
          this.toastrService.danger(
            mensagemDeErro(err, 'Não foi possível carregar as pessoas sem turma.'),
            'Erro',
          );
        },
      });
  }

  onPessoaChange(pessoaId: string) {
    this.pessoaSelecionada = this.pessoasSemTurma.find((p) => p.id === pessoaId);
  }

  adicionarPessoa() {
    if (!this.turmaSelecionada || !this.pessoaSelecionada) return;

    this.matriculaService
      .matricular(this.turmaSelecionada, this.pessoaSelecionada.id!)
      .subscribe({
        next: () => {
          // MSG-09: o nome está à mão; "Pessoa matriculada" não diz qual.
          const nome = this.pessoaSelecionada?.nome ?? 'Catequizando';
          this.pessoaSelecionada = undefined;
          this.carregarMatriculas();
          this.carregarPessoasSemTurma();
          this.toastrService.success(`${nome} matriculado nesta turma.`, 'Matrícula');
        },
        error: (err) => {
          this.toastrService.danger(mensagemDeErro(err, 'Não foi possível matricular a pessoa.'), 'Erro');
        },
      });
  }

  transferirPessoa(matricula: TurmaCatequizandoDto) {
    if (!this.turmaSelecionada) return;

    this.dialogService
      .open(DialogTransferirPessoaComponent, {
        context: {
          pessoaNome: matricula.pessoaNome,
          turmaAtualId: matricula.turmaId,
        },
      })
      .onClose.subscribe((novaTurmaId: string | null) => {
        if (!novaTurmaId) return;

        this.matriculaService
          .transferir({
            turmaSaida: matricula.turmaId,
            turmaEntrada: novaTurmaId,
            pessoa: matricula.pessoaId,
          })
          .subscribe({
            next: () => {
              this.carregarMatriculas();
              this.toastrService.success(
                `${matricula.pessoaNome} foi transferido(a) com sucesso.`,
                'Sucesso'
              );
            },
            error: (err) => {
              this.toastrService.danger(mensagemDeErro(err, 'Não foi possível transferir a pessoa.'), 'Erro');
            },
          });
      });
  }

  removerPessoa(matricula: TurmaCatequizandoDto) {
    if (!this.turmaSelecionada) return;

    this.dialogService
      .open(DialogAlertComponent, {
        context: {
          title: 'Confirmação',
          message: `Confirma a remoção de ${matricula.pessoaNome} da turma?`,
          mostrarConfirmacao: false,  // Não exibir checkbox
        },
      })
      .onClose.subscribe((confirmado: boolean) => {
        if (!confirmado) return;

        this.matriculaService
          .desmatricular(matricula.turmaId, matricula.pessoaId)
          .subscribe({
            next: () => {
              this.carregarMatriculas();
              this.toastrService.success(`${matricula.pessoaNome} saiu desta turma.`, 'Matrícula');
            },
            error: (err) => {
              this.toastrService.danger(mensagemDeErro(err, 'Não foi possível remover a pessoa da turma.'), 'Erro');
            },
          });
      });
  }

}
